import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Send, User, Bot, RefreshCw, PhoneCall, Sparkles, Share2, Check } from 'lucide-react';

/**
 * SupplierSimulator — a live "type as the supplier, watch Grocera reply" panel.
 * Uses POST /api/procurement/threads/simulate-inbound which already routes
 * through the LLM-backed agent (Bahasa reply).
 */
export default function SupplierSimulator({ api, autoplay = false }) {
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [poId, setPoId] = useState('');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {who: 'supplier'|'agent', body, action?, ts}
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);
  const autoplayFired = useRef(false);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/demo` : '/demo';
  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${api}/procurement/suppliers`),
        axios.get(`${api}/procurement/purchase-orders?state=SENT`),
      ]);
      setSuppliers(s.data || []);
      setPos(p.data || []);
      if (!supplierId && s.data?.[0]) setSupplierId(s.data[0].id);
    } catch { /* ignore */ }
  }, [api, supplierId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const supplier = useMemo(() => suppliers.find((s) => s.id === supplierId), [suppliers, supplierId]);
  const poForSup = useMemo(
    () => pos.filter((p) => p.supplier_id === supplierId),
    [pos, supplierId],
  );

  const send = async (overrideBody = null) => {
    const body = (overrideBody != null ? overrideBody : input).trim();
    if (!body || !supplierId || busy) return;
    setBusy(true);
    if (overrideBody == null) setInput('');
    const ts = new Date();
    setMessages((m) => [...m, { who: 'supplier', body, ts }]);
    try {
      const r = await axios.post(`${api}/procurement/threads/simulate-inbound`, {
        supplier_id: supplierId,
        body,
        po_id: poId || null,
      });
      const reply = r.data?.agent_reply || '(agent tidak membalas)';
      const action = r.data?.action;
      // small thinking delay for feel
      setTimeout(() => {
        setMessages((m) => [...m, { who: 'agent', body: reply, action, ts: new Date() }]);
        setBusy(false);
      }, 350);
    } catch (e) {
      setMessages((m) => [...m, { who: 'agent', body: 'Gagal menghubungi agent. Coba lagi.', ts: new Date() }]);
      setBusy(false);
    }
  };

  // Autoplay: as soon as we have a supplier picked and haven't fired yet,
  // send one canned message so the wow moment lands within ~1s of page load.
  useEffect(() => {
    if (!autoplay || autoplayFired.current || !supplierId) return;
    autoplayFired.current = true;
    const t = setTimeout(() => {
      send('Siap kak, PO nya kami konfirmasi. Kirim besok pagi.');
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, supplierId]);

  const reset = () => setMessages([]);

  const quickShots = [
    'Siap kak, PO nya kami konfirmasi. Kirim besok pagi.',
    'Maaf, stok apel fuji kurang 12 karton hari ini.',
    'Boleh ganti pisang ambon dengan pisang kepok?',
    'Truk kena macet di tol Cikampek, mundur 2 jam ya.',
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="supplier-simulator">
      {/* Header */}
      <div className="lg:col-span-12 flex items-center justify-between">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Sparkles size={12} className="text-[#7ba0ff]" /> LIVE · WhatsApp Simulator
          </div>
          <h2 className="h-display text-[28px]">Talk to Grocera as a supplier</h2>
          <p className="text-white/55 text-[13.5px] mt-1 max-w-[600px]">
            Pick a supplier, type a message in Bahasa or English, and watch the AI agent reply
            in real time. This is the exact same loop that runs on live WhatsApp.
          </p>
        </div>
        <button onClick={reset} className="btn-ghost text-[12px] flex items-center gap-1.5" data-testid="sim-reset">
          <RefreshCw size={13} /> Clear
        </button>
        <button onClick={onShare} className="btn-ghost text-[12px] flex items-center gap-1.5" data-testid="sim-share" title={shareUrl}>
          {copied ? <><Check size={13} className="text-emerald-400" /> Copied</> : <><Share2 size={13} /> Share demo</>}
        </button>
      </div>

      {/* Left rail: supplier + PO picker */}
      <div className="lg:col-span-4 border border-white/10 rounded-md bg-black/30 p-4 h-fit">
        <div className="mono text-[10px] tracking-widest text-white/50 uppercase mb-2">Play as supplier</div>
        <select
          value={supplierId}
          onChange={(e) => { setSupplierId(e.target.value); setPoId(''); }}
          className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-[13.5px] focus:border-[#2E6BFF] outline-none"
          data-testid="sim-supplier-select"
        >
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>{s.name} · {s.whatsapp_number}</option>
          ))}
        </select>

        {supplier && (
          <div className="mt-3 mono text-[11px] text-white/50 leading-relaxed">
            reliability {supplier.reliability_score} · tags {(supplier.tags || []).join(', ') || '—'}
          </div>
        )}

        <div className="mono text-[10px] tracking-widest text-white/50 uppercase mt-5 mb-2">
          Attach to PO <span className="text-white/30 normal-case tracking-normal">(optional)</span>
        </div>
        <select
          value={poId}
          onChange={(e) => setPoId(e.target.value)}
          className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-[13.5px] focus:border-[#2E6BFF] outline-none"
          data-testid="sim-po-select"
        >
          <option value="">— no PO —</option>
          {poForSup.map((p) => (
            <option key={p.id} value={p.id}>{p.id} · {p.state}</option>
          ))}
        </select>
        {poForSup.length === 0 && supplier && (
          <div className="mono text-[10.5px] text-white/40 mt-2">
            No PO in SENT state for this supplier — try one without an attached PO.
          </div>
        )}

        <div className="mono text-[10px] tracking-widest text-white/50 uppercase mt-6 mb-2">Quick messages</div>
        <div className="flex flex-col gap-1.5">
          {quickShots.map((q) => (
            <button
              key={q}
              onClick={() => setInput(q)}
              className="text-left px-2.5 py-1.5 border border-white/8 rounded text-[12px] text-white/70 hover:text-white hover:border-[#2E6BFF]/40 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* Right: conversation */}
      <div className="lg:col-span-8 border border-white/10 rounded-md bg-black/30 flex flex-col min-h-[560px]">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#2E6BFF]/15 border border-[#2E6BFF]/30 grid place-items-center">
            <PhoneCall size={13} className="text-[#7ba0ff]" />
          </div>
          <div>
            <div className="text-[13.5px] text-white">Grocera Agent · {supplier?.name || '—'}</div>
            <div className="mono text-[10px] text-white/45">via Fonnte · Bahasa Indonesia</div>
          </div>
          <span className="pill ml-auto"><span className="dot-live" /> LIVE</span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[540px]" data-testid="sim-chat">
          {messages.length === 0 && !busy && (
            <div className="text-center text-white/40 text-[13px] py-16">
              Type a message below or pick a quick message to start the conversation.
            </div>
          )}
          {messages.map((m, i) => (
            <Bubble key={i} m={m} />
          ))}
          {busy && (
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-full bg-[#2E6BFF]/15 border border-[#2E6BFF]/30 grid place-items-center flex-shrink-0">
                <Bot size={13} className="text-[#7ba0ff]" />
              </div>
              <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10">
                <span className="inline-flex gap-1">
                  <Dot delay={0} /><Dot delay={0.15} /><Dot delay={0.3} />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ketik pesan sebagai supplier…"
            className="flex-1 px-3 py-2.5 bg-black/40 border border-white/10 rounded text-white text-[13.5px] focus:border-[#2E6BFF] outline-none"
            data-testid="sim-input"
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || !supplierId || busy}
            className="btn-primary flex items-center gap-1.5 text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="sim-send"
          >
            <Send size={13} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

const Bubble = ({ m }) => {
  const isSupplier = m.who === 'supplier';
  return (
    <div className={`flex items-start gap-2 ${isSupplier ? 'flex-row-reverse' : ''}`}>
      <div className={`w-7 h-7 rounded-full grid place-items-center flex-shrink-0 border
        ${isSupplier ? 'bg-white/10 border-white/15' : 'bg-[#2E6BFF]/15 border-[#2E6BFF]/30'}`}>
        {isSupplier ? <User size={13} className="text-white/80" /> : <Bot size={13} className="text-[#7ba0ff]" />}
      </div>
      <div className={`px-3.5 py-2 rounded-lg max-w-[75%] text-[13.5px] leading-[1.55]
        ${isSupplier ? 'bg-[#2E6BFF]/12 border border-[#2E6BFF]/25 text-white' : 'bg-white/[0.04] border border-white/10 text-white/90'}`}>
        {m.body}
        {m.action && !isSupplier && (
          <div className="mono text-[9.5px] tracking-widest text-white/45 uppercase mt-1.5">
            ▸ action · {m.action}
          </div>
        )}
      </div>
    </div>
  );
};

const Dot = ({ delay }) => (
  <span
    className="inline-block w-1.5 h-1.5 rounded-full bg-white/45"
    style={{
      animation: 'dotBlink 1.2s ease-in-out infinite',
      animationDelay: `${delay}s`,
    }}
  />
);

// One-time keyframe injection (module scoped, safe under React double-mount)
if (typeof document !== 'undefined' && !document.getElementById('sim-anim')) {
  const s = document.createElement('style');
  s.id = 'sim-anim';
  s.textContent = `@keyframes dotBlink { 0%,80%,100%{opacity:0.25;transform:translateY(0)} 40%{opacity:1;transform:translateY(-2px)} }`;
  document.head.appendChild(s);
}
